/**
 * Staff entity config — drives EntityTable and EntityDetailShell.
 *
 * Mirrors src/features/people/config/studentsEntityConfig.tsx exactly.
 * staff_type is a free-text placeholder column here (built out in 11E.5 —
 * see StaffFilterConfig below); Salary/Pay tab visibility is admin/owner-only.
 */

import React from 'react';
import { StatusBadge } from '@/components/common/Badge';
import type {
  EntityTableConfig,
  EntityDetailConfig,
} from '@/components/entity/types';
import type { StaffResponse } from '@features/people/api/staff';
import { getFullName } from '@/utils';
import {
  StaffNameCell,
  RoleCell,
  StaffTypeCell,
  NotWiredPlaceholder,
} from './staffEntityComponents';

// ── EntityTableConfig ────────────────────────────────────────────────────

export const staffTableConfig: EntityTableConfig<StaffResponse> = {
  entityName: 'staff',
  apiPath: '/v1/staff',
  routeBase: '/staff',
  testIdPrefix: 'candeza-staff',
  defaultSort: 'created_at',
  defaultOrder: 'desc',
  defaultFilters: { status: 'active' },

  columns: [
    {
      key: 'name',
      header: 'Staff Member',
      isPrimary: true,
      sortable: true,
      helpKey: 'staff.name',
      render: (s) => <StaffNameCell staff={s} />,
    },
    {
      key: 'role',
      header: 'Role',
      helpKey: 'staff.role',
      render: (s) => <RoleCell staff={s} />,
    },
    {
      key: 'staff_type',
      header: 'Staff Type',
      hideOnMobile: true,
      helpKey: 'staff.staff_type',
      render: (s) => <StaffTypeCell staff={s} />,
    },
    {
      key: 'phone',
      header: 'Phone',
      hideOnMobile: true,
      render: (s) => s.phone || '—',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      helpKey: 'staff.status',
      render: (s) => <StatusBadge status={s.status} />,
    },
  ],

  filters: [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      helpKey: 'staff.filter.status',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
    },
    {
      key: 'role',
      label: 'Role',
      type: 'select',
      helpKey: 'staff.filter.role',
      options: [
        { value: 'all', label: 'All Roles' },
        { value: 'admin', label: 'Admin' },
        { value: 'manager', label: 'Manager' },
        { value: 'staff', label: 'Staff / Instructor' },
      ],
    },
  ],

  rowActions: ['view', 'edit', 'archive'],
};

// ── EntityDetailConfig ───────────────────────────────────────────────────
// Tabs use render-props injected at the route layer, same pattern as
// students — Staff must not import from sibling features directly.

interface StaffDetailTabProps {
  renderOverviewTab: (staffId: string) => React.ReactNode;
  renderStudentsAssignedTab?: (staffId: string) => React.ReactNode;
  renderWorkingHoursTab?: (staffId: string) => React.ReactNode;
  renderCalendarTab?: (staffId: string) => React.ReactNode;
  renderBookingsTab?: (staffId: string) => React.ReactNode;
  renderAttendanceLeavesTab?: (staffId: string) => React.ReactNode;
  renderNotesTab?: (staffId: string) => React.ReactNode;
  renderDocumentsTab?: (staffId: string) => React.ReactNode;
  /** Only included in config.tabs when the viewer is admin/owner. */
  renderSalaryPayTab?: (staffId: string) => React.ReactNode;
  /** 11F.7 — newly wired tabs. */
  renderTimesheetTab?: (staffId: string) => React.ReactNode;
  renderPayRatesTab?: (staffId: string) => React.ReactNode;
  renderSidebar?: (staff: StaffResponse) => React.ReactNode;
  /** Gate for the Salary/Pay tab — pass the current viewer's role. */
  viewerRole?: string;
}

export function buildStaffDetailConfig(
  props: StaffDetailTabProps,
): EntityDetailConfig<StaffResponse> {
  const canSeeSalary = props.viewerRole === 'owner' || props.viewerRole === 'admin';

  return {
    entityName: 'staff',
    routeBase: '/staff',
    entityType: 'staff',
    testIdPrefix: 'candeza-staff',

    getTitle: (s) => getFullName(s.first_name, s.last_name),
    getSubtitle: (s) => s.email,
    getStatus: (s) => s.status,
    getPhotoUrl: (s) => s.photo_url,
    renderSidebar: props.renderSidebar,

    tabs: [
      {
        id: 'overview',
        label: 'Overview',
        lazyLoad: false,
        render: props.renderOverviewTab,
      },
      {
        id: 'students',
        label: 'Students assigned',
        render: props.renderStudentsAssignedTab ?? NotWiredPlaceholder,
      },
      {
        id: 'working-hours',
        label: 'Working hours',
        render: props.renderWorkingHoursTab ?? NotWiredPlaceholder,
      },
      {
        id: 'calendar',
        label: 'Calendar',
        render: props.renderCalendarTab ?? NotWiredPlaceholder,
      },
      // 11F.7 — derived from taught sessions; see StaffTimesheetTab.
      {
        id: 'timesheet',
        label: 'Timesheet',
        render: props.renderTimesheetTab ?? NotWiredPlaceholder,
      },
      {
        id: 'bookings',
        label: 'Bookings',
        render: props.renderBookingsTab ?? NotWiredPlaceholder,
      },
      {
        id: 'attendance-leaves',
        label: 'Attendance & Leaves',
        helpKey: 'staff.attendance_leaves',
        // Read-only stub until 11E.6 lands staff attendance/leave management.
        render: props.renderAttendanceLeavesTab ?? (() => (
          <p style={{ color: '#64748b' }}>
            Attendance &amp; leave tracking lands in Prompt 11E.6. Read-only stub for now.
          </p>
        )),
      },
      {
        id: 'notes',
        label: 'Notes',
        render: props.renderNotesTab ?? NotWiredPlaceholder,
      },
      {
        id: 'documents',
        label: 'Documents',
        render: props.renderDocumentsTab ?? NotWiredPlaceholder,
      },
      ...(canSeeSalary
        ? [
            // 11F.7 — per-subject/per-duration rate card (ref staff3.png).
            // Behind the SAME gate as Salary/Pay: 11E.5's nav spec says
            // office managers have no salary visibility, and the backend
            // read endpoint is owner/admin/manager-only.
            {
              id: 'pay-rates',
              label: 'Subjects & Pay rates',
              render: props.renderPayRatesTab ?? NotWiredPlaceholder,
            },
            {
              id: 'salary-pay',
              label: 'Salary / Pay',
              helpKey: 'staff.salary_pay',
              render: props.renderSalaryPayTab ?? NotWiredPlaceholder,
            },
          ]
        : []),
    ],
  };
}

